import { OperatorElearningTopicContentTranslationService } from '../../../../../api/api-services/operator-elearning-topic-content-translation.service';
import { IOperatorElearningTopicContentTranslationIm } from '../../../../../api/operator-elearning-topic-content-translation-im.model';
import { OperatorElearningTopicCategoriesService } from '../../../../../api/api-services/operator-elearning-topic-categories.service';
import { OperatorElearningTopicContentsService } from '../../../../../api/api-services/operator-elearning-topic-contents.service';
import { OperatorElearningTopicContentService } from '../../../../../api/api-services/operator-elearning-topic-content.service';
import { OperatorElearningTopicsService } from '../../../../../api/api-services/operator-elearning-topics.service';
import { IOperatorElearningTopicCategoryVm } from '../../../../../api/operator-elearning-topic-category-vm.model';
import { IOperatorElearningTopicContentIm } from '../../../../../api/operator-elearning-topic-content-im.model';
import { IOperatorElearningTopicVm } from '../../../../../api/operator-elearning-topic-vm.model';
import { IExamViewModel } from '../../../../../api/exam-view-model.model';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { environment } from '../../../../../environments/environment';
import { Component, OnInit, inject } from '@angular/core';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { HttpClient } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';

interface UploadFileResultVm {
  publicVisible: boolean;
  success: boolean;
  size: number;
  fileUrl: string;
}

@Component({
  selector: 'app-add-elearning',
  templateUrl: './add-elearning.component.html',
  styleUrl: './add-elearning.component.scss'
})
export class AddElearningComponent implements OnInit {

  private _operatorElearningTopicContentTranslationService = inject(OperatorElearningTopicContentTranslationService);
  private _operatorElearningTopicCategoriesService = inject(OperatorElearningTopicCategoriesService);
  private _operatorElearningTopicContentsService = inject(OperatorElearningTopicContentsService);
  private _operatorElearningTopicContentService = inject(OperatorElearningTopicContentService);
  private _operatorElearningTopicsService = inject(OperatorElearningTopicsService);
  private _messageService = inject(MessageService);
  private _dialogRef = inject(DynamicDialogRef);
  private _http = inject(HttpClient);
  private _fb = inject(FormBuilder);
  
  public contentForm!: FormGroup;

  public showAddMediaLanguageDropdown = false;
  public selectedNewMediaLanguage = '';
  public environment = environment;
  public maxTranslations = 3;
  public loading = false;
  public currentStep = 1;

  public elearningTopicCategories: IOperatorElearningTopicCategoryVm[] = [];
  public availableTopics: IOperatorElearningTopicVm[] = [];

  private mediaLanguageCollapseState: { [key: string]: boolean } = {};
  public uploadedVideoFiles: { [languageCode: string]: string } = {};
  public uploadedAudioFiles: { [languageCode: string]: string } = {};
  public selectedImageFiles: { [languageCode: string]: File } = {};
  public selectedVideoFiles: { [languageCode: string]: File } = {};
  public selectedAudioFiles: { [languageCode: string]: File } = {};
  public uploadedImages: { [languageCode: string]: string } = {};
  public selectedImageLanguages: string[] = [];
  public selectedVideoLanguages: string[] = [];
  public selectedAudioLanguages: string[] = [];

  public translationLanguages = [
    { code: 'en', name: 'Angielski' },
    { code: 'uk', name: 'Ukraiński' },
    { code: 'de', name: 'Niemiecki' }
  ];

  ngOnInit(): void {
    this.initForms();
    this.loadCategories();
  }

  private initForms(): void {
    this.contentForm = this._fb.group({
      categoryId: ['', Validators.required],
      slideTitle: [''],
      slideContent: ['', Validators.required],
      elearningTopicId: ['', Validators.required],
      slideOrder: [null, [Validators.required, Validators.min(1)]],
      examsGuid: [[]],
      translations: this._fb.array([])
    });
  }

  public get availableExamsForSelectedCategory(): IExamViewModel[] {
    const categoryId = this.contentForm?.get('categoryId')?.value;
    if (!categoryId) {
      return [];
    }
    const category = this.elearningTopicCategories.find(c => c.id === categoryId);
    return category?.examCategories || [];
  }

  get translationsArray(): FormArray {
    return this.contentForm.get('translations') as FormArray;
  }

  createTranslationGroup(language: any): FormGroup {
    return this._fb.group({
      languageCode: [language.code, Validators.required],
      slideTitle: [''],
      slideContent: ['', Validators.required]
    });
  }

  addTranslation(language: any): void {
    const existingLanguage = this.translationsArray.controls.find(control => 
      control.get('languageCode')?.value === language.code
    );
    
    if (!existingLanguage) {
      const translationGroup = this.createTranslationGroup(language);
      this.translationsArray.push(translationGroup);
    }
  }

  removeTranslation(index: number): void {
    const translation = this.translationsArray.at(index);
    const languageCode = translation?.get('languageCode')?.value;
    
    this.translationsArray.removeAt(index);
    
    if (languageCode) {
      delete this.uploadedImages[languageCode];
      delete this.uploadedVideoFiles[languageCode];
      delete this.uploadedAudioFiles[languageCode];
      delete this.selectedImageFiles[languageCode];
      delete this.selectedVideoFiles[languageCode];
      delete this.selectedAudioFiles[languageCode];
      
      this.selectedImageLanguages = this.selectedImageLanguages.filter((lang: string) => lang !== languageCode);
      this.selectedVideoLanguages = this.selectedVideoLanguages.filter((lang: string) => lang !== languageCode);
      this.selectedAudioLanguages = this.selectedAudioLanguages.filter((lang: string) => lang !== languageCode);
    }
  }

  public addNewTranslationForm(): void {
    if (this.translationsArray.length < this.maxTranslations) {
      const translationGroup = this._fb.group({
        languageCode: ['', [Validators.required]],
        slideTitle: [''],
        slideContent: ['', [Validators.required]],
        collapsed: [false]
      });
      this.translationsArray.push(translationGroup);
    }
  }

  public removeTranslationForm(index: number): void {
    this.translationsArray.removeAt(index);
  }

  public toggleTranslationCollapse(index: number): void {
    const translation = this.translationsArray.at(index);
    if (translation) {
      const currentCollapsed = translation.get('collapsed')?.value;
      translation.get('collapsed')?.setValue(!currentCollapsed);
    }
  }

  public isTranslationCollapsed(index: number): boolean {
    const translation = this.translationsArray.at(index);
    return translation ? translation.get('collapsed')?.value : false;
  }

  public canAddMoreTranslations(): boolean {
    return this.translationsArray.length < this.maxTranslations;
  }

  public getAvailableLanguagesForTranslation(currentIndex?: number) {
    const used = this.translationsArray.controls
      .map((control, idx) => idx === currentIndex ? null : control.get('languageCode')?.value)
      .filter((code): code is string => !!code);

    return this.translationLanguages.filter(lang =>
      !used.includes(lang.code)
    );
  }

  public onTopicChange(topicId: number): void {
    if (topicId) {
      this.updateOrderBasedOnTopic(topicId);
    }
  }

  private updateOrderBasedOnTopic(topicId: number): void {
    this._operatorElearningTopicContentsService.get(topicId).subscribe({
      next: (contents) => {
        const maxOrder = contents.length > 0
          ? Math.max(...contents.map(c => c.slideOrder || 0))
          : 0;
        const nextOrder = maxOrder + 1;
        this.contentForm.patchValue({ slideOrder: nextOrder });
      },
      error: (error) => {
        this.contentForm.patchValue({ slideOrder: 1 });
      }
    });
  }

  private loadCategories(): void {
    this._operatorElearningTopicCategoriesService.get().subscribe({
      next: (categories: IOperatorElearningTopicCategoryVm[]) => {
        this.elearningTopicCategories = categories;
      },
      error: (error: any) => {
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Nie udało się załadować działów'
        });
      }
    });
  }

  public onCategoryChange(categoryId: number): void {
    this.contentForm.patchValue({
      elearningTopicId: null,
      slideOrder: null,
      examsGuid: []
    });
    this.availableTopics = [];
    
    if (categoryId) {
      this._operatorElearningTopicsService.get(categoryId).subscribe({
        next: (topics: IOperatorElearningTopicVm[]) => {
          this.availableTopics = topics;
        },
        error: (error: any) => {
          this._messageService.add({
            severity: 'error',
            summary: 'Błąd',
            detail: 'Nie udało się załadować rozdziałów'
          });
        }
      });
    }
  }

  public nextStep(): void {
    if (this.currentStep === 1 && this.canProceedToNextStep()) {
      this.currentStep++;
    }
  }

  public canProceedToNextStep(): boolean {
    if (!this.contentForm.valid) {
      return false;
    }

    for (let i = 0; i < this.translationsArray.length; i++) {
      const translation = this.translationsArray.at(i);
      const languageCode = translation.get('languageCode')?.value;
      const slideContent = translation.get('slideContent')?.value;

      if (languageCode && (!slideContent || slideContent.trim().length < 2)) {
        return false;
      }
    }

    return true;
  }

  public previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
      
      if (this.currentStep === 1) {
        this.restoreCategoryAndTopics();
      }
    }
  }

  private restoreCategoryAndTopics(): void {
    const selectedCategoryId = this.contentForm.get('categoryId')?.value;
    
    if (selectedCategoryId) {
      this._operatorElearningTopicsService.get(selectedCategoryId).subscribe({
        next: (topics: IOperatorElearningTopicVm[]) => {
          this.availableTopics = topics;
        },
        error: (error: any) => {
          this._messageService.add({
            severity: 'error',
            summary: 'Błąd',
            detail: 'Nie udało się załadować rozdziałów'
          });
        }
      });
    }
  }

  public onImageSelected(files: File | File[], languageCode: string = 'pl'): void {
    
    if (files instanceof File) {
      this.selectedImageFiles[languageCode] = files;
      this._messageService.add({
        severity: 'info',
        summary: 'Plik wybrany',
        detail: `Wybrano obraz: ${files.name} dla języka ${this.getLanguageName(languageCode)}`
      });
    } else if (Array.isArray(files) && files.length > 0) {
      this.selectedImageFiles[languageCode] = files[0];
      this._messageService.add({
        severity: 'info',
        summary: 'Plik wybrany',
        detail: `Wybrano obraz: ${files[0].name} dla języka ${this.getLanguageName(languageCode)}`
      });
    }
  }

  public onVideoSelected(files: File | File[], languageCode: string = 'pl'): void {
    
    if (files instanceof File) {
      this.selectedVideoFiles[languageCode] = files;
      this._messageService.add({
        severity: 'info',
        summary: 'Plik wybrany',
        detail: `Wybrano wideo: ${files.name} dla języka ${this.getLanguageName(languageCode)}`
      });
    } else if (Array.isArray(files) && files.length > 0) {
      this.selectedVideoFiles[languageCode] = files[0];
      this._messageService.add({
        severity: 'info',
        summary: 'Plik wybrany',
        detail: `Wybrano wideo: ${files[0].name} dla języka ${this.getLanguageName(languageCode)}`
      });
    }
  }

  public onAudioSelected(files: File | File[], languageCode: string = 'pl'): void {
    
    if (files instanceof File) {
      this.selectedAudioFiles[languageCode] = files;
      this._messageService.add({
        severity: 'info',
        summary: 'Plik wybrany',
        detail: `Wybrano audio: ${files.name} dla języka ${this.getLanguageName(languageCode)}`
      });
    } else if (Array.isArray(files) && files.length > 0) {
      this.selectedAudioFiles[languageCode] = files[0];
      this._messageService.add({
        severity: 'info',
        summary: 'Plik wybrany',
        detail: `Wybrano audio: ${files[0].name} dla języka ${this.getLanguageName(languageCode)}`
      });
    }
  }

  public getSelectedImageLanguages(): string[] {
    return this.selectedImageLanguages;
  }

  public getSelectedVideoLanguages(): string[] {
    return this.selectedVideoLanguages;
  }

  public getSelectedAudioLanguages(): string[] {
    return this.selectedAudioLanguages;
  }

  public getAvailableLanguagesForImages() {
    return this.translationLanguages.filter(lang => 
      !this.selectedImageLanguages.includes(lang.code)
    );
  }

  public getAvailableLanguagesForVideos() {
    return this.translationLanguages.filter(lang => 
      !this.selectedVideoLanguages.includes(lang.code)
    );
  }

  public getAvailableLanguagesForAudios() {
    return this.translationLanguages.filter(lang => 
      !this.selectedAudioLanguages.includes(lang.code)
    );
  }

  public addImageLanguage(languageCode: string): void {
    if (languageCode && !this.selectedImageLanguages.includes(languageCode)) {
      if (!this.canAddMediaForLanguage(languageCode)) {
        this._messageService.add({
          severity: 'warn',
          summary: 'Uwaga',
          detail: `Nie można dodać obrazów dla języka ${this.getLanguageName(languageCode)}. Najpierw uzupełnij treść slajdu w tym języku.`
        });
        return;
      }
      this.selectedImageLanguages.push(languageCode);
    }
  }

  public addVideoLanguage(languageCode: string): void {
    if (languageCode && !this.selectedVideoLanguages.includes(languageCode)) {
      if (!this.canAddMediaForLanguage(languageCode)) {
        this._messageService.add({
          severity: 'warn',
          summary: 'Uwaga',
          detail: `Nie można dodać wideo dla języka ${this.getLanguageName(languageCode)}. Najpierw uzupełnij treść slajdu w tym języku.`
        });
        return;
      }
      this.selectedVideoLanguages.push(languageCode);
    }
  }

  public addAudioLanguage(languageCode: string): void {
    if (languageCode && !this.selectedAudioLanguages.includes(languageCode)) {
      if (!this.canAddMediaForLanguage(languageCode)) {
        this._messageService.add({
          severity: 'warn',
          summary: 'Uwaga',
          detail: `Nie można dodać audio dla języka ${this.getLanguageName(languageCode)}. Najpierw uzupełnij treść slajdu w tym języku.`
        });
        return;
      }
      this.selectedAudioLanguages.push(languageCode);
    }
  }

  public removeImageLanguage(languageCode: string): void {
    const index = this.selectedImageLanguages.indexOf(languageCode);
    if (index > -1) {
      this.selectedImageLanguages.splice(index, 1);
      delete this.selectedImageFiles[languageCode];
      delete this.uploadedImages[languageCode];
    }
  }

  public removeVideoLanguage(languageCode: string): void {
    const index = this.selectedVideoLanguages.indexOf(languageCode);
    if (index > -1) {
      this.selectedVideoLanguages.splice(index, 1);
      delete this.selectedVideoFiles[languageCode];
      delete this.uploadedVideoFiles[languageCode];
    }
  }

  public removeAudioLanguage(languageCode: string): void {
    const index = this.selectedAudioLanguages.indexOf(languageCode);
    if (index > -1) {
      this.selectedAudioLanguages.splice(index, 1);
      delete this.selectedAudioFiles[languageCode];
      delete this.uploadedAudioFiles[languageCode];
    }
  }

  public getSelectedMediaLanguages(): string[] {
    const allLanguages = new Set([
      ...this.selectedImageLanguages,
      ...this.selectedVideoLanguages,
      ...this.selectedAudioLanguages
    ]);
    return Array.from(allLanguages);
  }

  public toggleMediaLanguageCollapse(languageCode: string): void {
    this.mediaLanguageCollapseState[languageCode] = !this.mediaLanguageCollapseState[languageCode];
  }

  public isMediaLanguageCollapsed(languageCode: string): boolean {
    return this.mediaLanguageCollapseState[languageCode] || false;
  }

  public canAddMoreMediaLanguages(): boolean {
    return this.getAvailableLanguagesForMedia().length > 0;
  }


  public getLanguagesWithBasicData(): string[] {
    return this.translationsArray.controls
      .filter(control => {
        const languageCode = control.get('languageCode')?.value;
        const slideContent = control.get('slideContent')?.value;
        return languageCode && slideContent && slideContent.trim().length >= 2; // minimalna długość treści
      })
      .map(control => control.get('languageCode')?.value)
      .filter((code): code is string => !!code);
  }

  public canAddMediaForLanguage(languageCode: string): boolean {
    return this.getLanguagesWithBasicData().includes(languageCode);
  }

  public getAvailableLanguagesForMedia() {
    const allUsedLanguages = new Set([
      ...this.selectedImageLanguages,
      ...this.selectedVideoLanguages,
      ...this.selectedAudioLanguages
    ]);
    const languagesWithBasicData = this.getLanguagesWithBasicData();
    return this.translationLanguages.filter(lang => 
      !allUsedLanguages.has(lang.code) &&
      languagesWithBasicData.includes(lang.code)
    );
  }

  public removeMediaLanguage(languageCode: string): void {
    this.removeImageLanguage(languageCode);
    this.removeVideoLanguage(languageCode);
    this.removeAudioLanguage(languageCode);
    delete this.mediaLanguageCollapseState[languageCode];
  }

  public onNewMediaLanguageSelected(): void {
    if (this.selectedNewMediaLanguage) {
      if (!this.canAddMediaForLanguage(this.selectedNewMediaLanguage)) {
        this._messageService.add({
          severity: 'warn',
          summary: 'Uwaga',
          detail: `Nie można dodać mediów dla języka ${this.getLanguageName(this.selectedNewMediaLanguage)}. Najpierw uzupełnij treść slajdu w tym języku.`
        });
        this.selectedNewMediaLanguage = '';
        this.showAddMediaLanguageDropdown = false;
        return;
      }
      
      this.selectedImageLanguages.push(this.selectedNewMediaLanguage);
      this.selectedVideoLanguages.push(this.selectedNewMediaLanguage);
      this.selectedAudioLanguages.push(this.selectedNewMediaLanguage);
      this.selectedNewMediaLanguage = '';
      this.showAddMediaLanguageDropdown = false;
    }
  }

  private async uploadFileToServer(file: File, languageCode: string): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('publicVisible', 'true');
      
      const response = await firstValueFrom(
        this._http.post<UploadFileResultVm>(`${environment.webApiUrl}/UploadMedia`, formData)
      );

      if (response.success) {
        this._messageService.add({
          severity: 'success',
          summary: 'Sukces',
          detail: `Plik dla języka ${this.getLanguageName(languageCode)} został przesłany.`
        });
        return response.fileUrl;
      } else {
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Nie udało się przesłać pliku.'
        });
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      this._messageService.add({
        severity: 'error',
        summary: 'Błąd',
        detail: 'Wystąpił błąd podczas przesyłania pliku.'
      });
      throw error;
    }
  }

  async uploadAllFiles(): Promise<void> {
    if (this.selectedImageFiles['pl']) {
      const url = await this.uploadFileToServer(this.selectedImageFiles['pl'], 'pl');
      this.uploadedImages['pl'] = url;
    }

    for (const languageCode of this.selectedImageLanguages) {
      if (this.selectedImageFiles[languageCode]) {
        const url = await this.uploadFileToServer(this.selectedImageFiles[languageCode], languageCode);
        this.uploadedImages[languageCode] = url;
      }
    }

    if (this.selectedVideoFiles['pl']) {
      const url = await this.uploadFileToServer(this.selectedVideoFiles['pl'], 'pl');
      this.uploadedVideoFiles['pl'] = url;
    }

    for (const languageCode of this.selectedVideoLanguages) {
      if (this.selectedVideoFiles[languageCode]) {
        const url = await this.uploadFileToServer(this.selectedVideoFiles[languageCode], languageCode);
        this.uploadedVideoFiles[languageCode] = url;
      }
    }

    if (this.selectedAudioFiles['pl']) {
      const url = await this.uploadFileToServer(this.selectedAudioFiles['pl'], 'pl');
      this.uploadedAudioFiles['pl'] = url;
    }

    for (const languageCode of this.selectedAudioLanguages) {
      if (this.selectedAudioFiles[languageCode]) {
        const url = await this.uploadFileToServer(this.selectedAudioFiles[languageCode], languageCode);
        this.uploadedAudioFiles[languageCode] = url;
      }
    }
  }

  async save(): Promise<void> {
    if (this.contentForm.valid) {
      this.loading = true;
      
      try {
        await this.uploadAllFiles();

        const contentData: IOperatorElearningTopicContentIm = {
          slideTitle: this.contentForm.get('slideTitle')?.value,
          slideContent: this.contentForm.get('slideContent')?.value,
          elearningTopicId: this.contentForm.get('elearningTopicId')?.value,
          slideOrder: this.contentForm.get('slideOrder')?.value,
          imageLink: this.uploadedImages['pl'] || '',
          mediaLink: this.uploadedVideoFiles['pl'] || '',
          audioLink: this.uploadedAudioFiles['pl'] || '',
          examsGuid: this.contentForm.get('examsGuid')?.value || []
        };

        const createdContent = await firstValueFrom(
          this._operatorElearningTopicContentService.post(contentData)
        );

        for (let i = 0; i < this.translationsArray.length; i++) {
          const translationForm = this.translationsArray.at(i);
          const languageCode = translationForm.get('languageCode')?.value;
          
          const translationData: IOperatorElearningTopicContentTranslationIm = {
            elearningTopicContentId: (createdContent as any).id,
            languageCode: languageCode,
            slideTitleOverride: '',
            slideContentOverride: translationForm.get('slideContent')?.value,
            imageLinkOverride: this.uploadedImages[languageCode] || '',
            mediaLinkOverride: this.uploadedVideoFiles[languageCode] || '',
            audioLinkOverride: this.uploadedAudioFiles[languageCode] || ''
          };

          await firstValueFrom(
            this._operatorElearningTopicContentTranslationService.post(translationData)
          );
        }

        this._messageService.add({
          severity: 'success',
          summary: 'Sukces',
          detail: 'Content został utworzony pomyślnie'
        });

        this._dialogRef.close(true);
      } catch (error) {
        console.error('Błąd podczas tworzenia contentu:', error);
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Nie udało się utworzyć contentu'
        });
      } finally {
        this.loading = false;
      }
    } else {
      this._messageService.add({
        severity: 'warn',
        summary: 'Uwaga',
        detail: 'Wypełnij wszystkie wymagane pola'
      });
    }
  }

  public cancel(): void {
    this._dialogRef.close(false);
  }

  public closeDialog(): void {
    this._dialogRef.close(false);
  }

  getLanguageName(code: string): string {
    if (code === 'pl') return 'Polski';
    const language = this.translationLanguages.find(lang => lang.code === code);
    return language ? language.name : code;
  }

  isFieldInvalid(controlName: string): boolean {
    const control = this.contentForm.get(controlName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  isTranslationFieldInvalid(index: number, fieldName: string): boolean {
    const translationGroup = this.translationsArray.at(index);
    const control = translationGroup?.get(fieldName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  getAvailableLanguagesForFiles(): any[] {
    const addedLanguages = this.translationsArray.controls.map(control => 
      control.get('languageCode')?.value
    );
    const availableLanguages = [
      { code: 'pl', name: 'Polski' },
      ...this.translationLanguages.filter(lang => addedLanguages.includes(lang.code))
    ];
    return availableLanguages;
  }

  hasAnyFiles(): boolean {
    return Object.keys(this.selectedImageFiles).length > 0 ||
           Object.keys(this.selectedVideoFiles).length > 0 ||
           Object.keys(this.selectedAudioFiles).length > 0;
  }

  getLanguagesWithFiles(): string[] {
    const languagesWithFiles = new Set<string>();
    
    Object.keys(this.selectedImageFiles).forEach(lang => languagesWithFiles.add(lang));
    Object.keys(this.selectedVideoFiles).forEach(lang => languagesWithFiles.add(lang));
    Object.keys(this.selectedAudioFiles).forEach(lang => languagesWithFiles.add(lang));
    
    return Array.from(languagesWithFiles);
  }

  removeFile(fileType: 'image' | 'video' | 'audio', languageCode: string): void {
    switch (fileType) {
      case 'image':
        delete this.selectedImageFiles[languageCode];
        delete this.uploadedImages[languageCode];
        this.selectedImageLanguages = this.selectedImageLanguages.filter(lang => lang !== languageCode);
        break;
      case 'video':
        delete this.selectedVideoFiles[languageCode];
        delete this.uploadedVideoFiles[languageCode];
        this.selectedVideoLanguages = this.selectedVideoLanguages.filter((lang: string) => lang !== languageCode);
        break;
      case 'audio':
        delete this.selectedAudioFiles[languageCode];
        delete this.uploadedAudioFiles[languageCode];
        this.selectedAudioLanguages = this.selectedAudioLanguages.filter((lang: string) => lang !== languageCode);
        break;
    }
  }
}
